import React from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { CheckCheck, Bell, Sparkles, Clock, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

interface NotificationDropdownProps {
    onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
    const { notifications, isLoadingNotifications, markAsRead, markAllAsRead } = useNotifications();

    return (
        <motion.div 
            className="absolute right-0 mt-3 w-96 origin-top-right rounded-3xl bg-white/95 dark:bg-slate-800/95 backdrop-blur-lg border border-slate-200/80 dark:border-slate-600/30 shadow-2xl overflow-hidden z-[110]"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
        >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
            
            {/* Header */}
            <div className="relative p-6 border-b border-slate-200 dark:border-slate-600/30">
                <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <Bell className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">Notifications</h3>
                    </div>
                    <motion.button
                        onClick={() => markAllAsRead()}
                        className="flex items-center space-x-2 px-3 py-1.5 bg-slate-200/60 dark:bg-slate-700/50 hover:bg-slate-300/60 dark:hover:bg-slate-600/50 rounded-xl border border-slate-300/80 dark:border-slate-600/50 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-all duration-200"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">Mark all read</span>
                    </motion.button>
                </div>
            </div>

            {/* Content */}
            <div className="relative max-h-96 overflow-y-auto">
                {isLoadingNotifications ? (
                    <div className="p-8 text-center">
                        <div className="w-12 h-12 border-2 border-slate-300/80 dark:border-slate-600/30 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-slate-500 dark:text-slate-400">Loading notifications...</p>
                    </div>
                ) : notifications && notifications.length > 0 ? (
                    <div className="divide-y divide-slate-200 dark:divide-slate-600/20">
                        <AnimatePresence>
                            {notifications.map((notification, index) => (
                                <motion.div
                                    key={notification.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                    className={`relative p-4 hover:bg-slate-200/30 dark:hover:bg-slate-700/30 transition-all duration-200 cursor-pointer group ${
                                        notification.is_active ? 'bg-indigo-500/10' : ''
                                    }`}
                                    onClick={() => {
                                        if (notification.is_active) {
                                            markAsRead(notification.id);
                                        }
                                    }}
                                    whileHover={{ x: 5 }}
                                >
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                                notification.is_active 
                                                    ? 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30' 
                                                    : 'bg-slate-200/60 dark:bg-slate-700/50 border border-slate-300/80 dark:border-slate-600/50'
                                            }`}>
                                                <Bell className={`w-5 h-5 ${
                                                    notification.is_active ? 'text-indigo-400' : 'text-slate-500 dark:text-slate-400'
                                                }`} />
                                            </div>
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                                {notification.message}
                                            </p>
                                            <div className="flex items-center space-x-2 mt-2">
                                                <Clock className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {notification.is_active && (
                                            <div className="flex-shrink-0">
                                                <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                ) : (
                    <motion.div 
                        className="p-12 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="w-16 h-16 bg-slate-200/60 dark:bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Bell className="w-8 h-8 text-slate-500 dark:text-slate-400" />
                        </div>
                        <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-300 mb-2">All caught up!</h4>
                        <p className="text-sm text-slate-500 dark:text-slate-400">No new notifications at the moment.</p>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};
